import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Sparkles, Crown, PauseCircle, PlayCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { billingApi } from './api';

// Tier presentation map — single source of truth, no nested ternaries
const TIER_DISPLAY = {
  free: {
    name: 'Apex Free',
    cardClass: 'bg-gradient-to-br from-[#0a0d0e] to-[#0f1416] border-[#0a0d0e] text-white',
    iconWrapClass: 'bg-[#004C54]/20 border border-[#004C54]/40',
    icon: <Sparkles className="h-5 w-5 text-[#4FBCC7]" />,
    labelClass: 'text-[#A5ACAF]',
    titleClass: 'text-white',
    metaClass: 'text-white/60',
  },
  pro: {
    name: 'Apex AI',
    cardClass: 'border-2 border-[#004C54]/30',
    iconWrapClass: 'bg-[#004C54]',
    icon: <Sparkles className="h-5 w-5 text-white" />,
    labelClass: 'text-[#004C54]',
    titleClass: 'text-foreground',
    metaClass: 'text-foreground/60',
  },
  premium: {
    name: '1:1 with Coach Lewis',
    cardClass: 'border-2 border-[#004C54]/30',
    iconWrapClass: 'bg-[#004C54]',
    icon: <Crown className="h-5 w-5 text-white" />,
    labelClass: 'text-[#004C54]',
    titleClass: 'text-foreground',
    metaClass: 'text-foreground/60',
  },
};

const PAUSE_OPTIONS = [
  { value: '1', label: '1 month', description: 'Auto-resumes in 30 days' },
  { value: '3', label: '3 months', description: 'Auto-resumes in 90 days' },
  { value: '6', label: '6 months', description: 'Auto-resumes in 180 days' },
  { value: 'indefinite', label: 'Indefinite', description: 'Resume manually whenever you’re ready' },
];

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString();
}

function PauseSubscriptionDialog({ onPaused }) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('1');
  const [submitting, setSubmitting] = useState(false);

  const handlePause = async () => {
    setSubmitting(true);
    try {
      const months = choice === 'indefinite' ? null : Number(choice);
      const data = await billingApi.pauseSubscription(months);
      toast.success(
        data.paused_resumes_at
          ? `Subscription paused. Auto-resumes on ${formatDate(data.paused_resumes_at)}.`
          : 'Subscription paused. Resume anytime from your dashboard.'
      );
      setOpen(false);
      onPaused?.();
    } catch (e) {
      toast.error(e.message || 'Could not pause subscription.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="pause-subscription-trigger">
          <PauseCircle className="h-4 w-4 mr-1.5" />
          Pause
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="pause-subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Pause your subscription</DialogTitle>
          <DialogDescription>
            You won’t be billed while paused. You keep access during the pause window and your
            plan will resume automatically (or whenever you click Resume).
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={choice} onValueChange={setChoice} className="space-y-2 py-2">
          {PAUSE_OPTIONS.map((opt) => (
            <Label
              key={opt.value}
              htmlFor={`pause-opt-${opt.value}`}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                choice === opt.value
                  ? 'border-[#004C54] bg-[#004C54]/5'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <RadioGroupItem
                value={opt.value}
                id={`pause-opt-${opt.value}`}
                data-testid={`pause-opt-${opt.value}`}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">{opt.label}</div>
                <div className="text-xs text-foreground/60 mt-0.5">{opt.description}</div>
              </div>
            </Label>
          ))}
        </RadioGroup>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
            data-testid="pause-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePause}
            disabled={submitting}
            className="bg-[#004C54] hover:bg-[#003a40] text-white"
            data-testid="pause-confirm"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Pause subscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SubscriptionCard({ sub, onUpgrade, onManage, onChanged }) {
  const tier = sub.tier || 'free';
  const display = TIER_DISPLAY[tier] || TIER_DISPLAY.free;
  const isTrial = sub.subscription_status === 'trialing';
  const planLabel = isTrial ? 'Free Trial' : 'Current Plan';
  const isFree = tier === 'free';
  const isPaused = Boolean(sub.is_paused);
  const isGrant = Boolean(sub.granted_by_admin);
  const canPause = !isFree && !isGrant && !isPaused && !sub.cancel_at_period_end;
  const [resuming, setResuming] = useState(false);

  const handleResume = async () => {
    setResuming(true);
    try {
      await billingApi.resumeSubscription();
      toast.success('Subscription resumed. Welcome back!');
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Could not resume subscription.');
    } finally {
      setResuming(false);
    }
  };

  return (
    <Card className={display.cardClass} data-testid="subscription-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${display.iconWrapClass}`}>
              {display.icon}
            </div>
            <div>
              <div className={`font-mono-accent uppercase tracking-[0.18em] text-[10px] font-bold ${display.labelClass}`}>
                {planLabel}
              </div>
              <div className={`font-heading text-lg font-bold ${display.titleClass}`}>
                {display.name}
              </div>
              {sub.trial_end && isTrial && (
                <div className={`text-xs mt-0.5 ${display.metaClass}`}>
                  Trial ends {formatDate(sub.trial_end)}
                </div>
              )}
              {sub.cancel_at_period_end && sub.current_period_end && (
                <div className="text-xs mt-0.5 text-destructive">
                  Cancels {formatDate(sub.current_period_end)}
                </div>
              )}
              {isPaused && (
                <div
                  className={`text-xs mt-0.5 font-semibold ${tier === 'free' ? 'text-[#4FBCC7]' : 'text-[#004C54]'}`}
                  data-testid="paused-indicator"
                >
                  {sub.paused_resumes_at
                    ? `Paused · auto-resumes ${formatDate(sub.paused_resumes_at)}`
                    : 'Paused · resume anytime'}
                </div>
              )}
            </div>
          </div>

          {isFree && (
            <Button
              onClick={onUpgrade}
              className="bg-white text-[#0a0d0e] hover:bg-white/90 font-semibold"
              data-testid="upgrade-cta"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              Upgrade
            </Button>
          )}

          {!isFree && (
            <div className="flex flex-wrap items-center gap-2">
              {isPaused && (
                <Button
                  onClick={handleResume}
                  disabled={resuming}
                  className="bg-[#004C54] hover:bg-[#003a40] text-white"
                  data-testid="resume-subscription"
                >
                  {resuming ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <PlayCircle className="h-4 w-4 mr-1.5" />
                  )}
                  Resume
                </Button>
              )}
              {canPause && <PauseSubscriptionDialog onPaused={onChanged} />}
              {!isGrant && (
                <Button onClick={onManage} variant="outline" data-testid="manage-subscription">
                  Manage subscription
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
